import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { products } from "@/content/catalog";
import type { CartItem } from "@/domain/schemas";

const CART_STORAGE_KEY = "living1991.demo.cart.v1";
const WISHLIST_STORAGE_KEY = "living1991.demo.wishlist.v1";

export interface DemoUser {
  name: string;
  email: string;
}

export function updateDemoUser(
  current: DemoUser,
  update: DemoUser,
): DemoUser {
  return {
    name: update.name.trim() || current.name,
    email: update.email.trim().toLowerCase() || current.email,
  };
}

export interface DemoOrder {
  id: string;
  status: "mock_confirmed" | "preparing" | "delivered";
  items: CartItem[];
  total: number;
  createdAt: string;
  payment: "mock_authorized";
  logistics: "mock_pending";
  invoice: "disabled";
}

export function addCartItem(
  items: CartItem[],
  productId: string,
  quantity = 1,
): CartItem[] {
  const parsedQuantity = Math.max(1, Math.min(99, Math.trunc(quantity)));
  const current = items.find((item) => item.productId === productId);
  if (!current) return [...items, { productId, quantity: parsedQuantity }];
  return items.map((item) =>
    item.productId === productId
      ? { ...item, quantity: Math.min(99, item.quantity + parsedQuantity) }
      : item,
  );
}

export function removeCartItem(items: CartItem[], productId: string): CartItem[] {
  return items.filter((item) => item.productId !== productId);
}

export function createOrderFromCart(
  items: CartItem[],
  idempotencyKey: string,
): DemoOrder {
  const validItems = items.filter(isCartItem).slice(0, 100);
  return {
    id: `DEMO-${idempotencyKey}`,
    status: "mock_confirmed",
    items: validItems,
    total: calculateCartTotal(validItems),
    createdAt: "2026-07-24T00:00:00.000Z",
    payment: "mock_authorized",
    logistics: "mock_pending",
    invoice: "disabled",
  };
}

interface DemoStoreValue {
  cart: CartItem[];
  cartCount: number;
  cartTotal: number;
  wishlist: string[];
  user: DemoUser | null;
  orders: DemoOrder[];
  lastOrder: DemoOrder | null;
  addToCart: (productId: string, quantity?: number) => boolean;
  updateQuantity: (productId: string, quantity: number) => void;
  removeFromCart: (productId: string) => void;
  toggleWishlist: (productId: string) => void;
  login: (email: string) => void;
  updateProfile: (update: DemoUser) => void;
  logout: () => void;
  checkout: (idempotencyKey: string) => DemoOrder | null;
}

const DemoStoreContext = createContext<DemoStoreValue | null>(null);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isCartItem(value: unknown): value is CartItem {
  if (!isRecord(value)) return false;
  return (
    typeof value.productId === "string" &&
    value.productId.length >= 1 &&
    value.productId.length <= 120 &&
    typeof value.quantity === "number" &&
    Number.isInteger(value.quantity) &&
    value.quantity >= 1 &&
    value.quantity <= 99
  );
}

function calculateCartTotal(items: CartItem[]): number {
  return items.reduce((total, item) => {
    const amount = products.find((product) => product.id === item.productId)?.price?.amount;
    return total + (amount ?? 0) * item.quantity;
  }, 0);
}

function readWishlist(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const value: unknown = JSON.parse(
      window.localStorage.getItem(WISHLIST_STORAGE_KEY) ?? "[]",
    );
    if (!Array.isArray(value)) return [];
    return value
      .filter(
        (item): item is string =>
          typeof item === "string" && item.length >= 1 && item.length <= 120,
      )
      .slice(0, 100);
  } catch {
    return [];
  }
}

function readCart(): CartItem[] {
  if (typeof window === "undefined") return [];
  try {
    const value: unknown = JSON.parse(
      window.localStorage.getItem(CART_STORAGE_KEY) ?? "[]",
    );
    if (!Array.isArray(value)) return [];
    return value.filter(isCartItem).slice(0, 100);
  } catch {
    return [];
  }
}

const initialOrders: DemoOrder[] = [
  {
    id: "DEMO-20260724-001",
    status: "preparing",
    items: [{ productId: "c-bento", quantity: 1 }],
    total: 1090,
    createdAt: "2026-07-24T00:00:00.000Z",
    payment: "mock_authorized",
    logistics: "mock_pending",
    invoice: "disabled",
  },
];

export function DemoStoreProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<CartItem[]>(readCart);
  const [wishlist, setWishlist] = useState<string[]>(readWishlist);
  const [user, setUser] = useState<DemoUser | null>(null);
  const [orders, setOrders] = useState<DemoOrder[]>(initialOrders);
  const [lastOrder, setLastOrder] = useState<DemoOrder | null>(null);

  useEffect(() => {
    window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    window.localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(wishlist));
  }, [wishlist]);

  const addToCart = useCallback((productId: string, quantity = 1) => {
    const product = products.find((item) => item.id === productId);
    if (!product?.price || product.inventory.status === "out_of_stock") return false;
    setCart((items) => addCartItem(items, productId, quantity));
    return true;
  }, []);

  const updateQuantity = useCallback((productId: string, quantity: number) => {
    if (quantity <= 0) {
      setCart((items) => removeCartItem(items, productId));
      return;
    }
    setCart((items) =>
      items.map((item) =>
        item.productId === productId
          ? { ...item, quantity: Math.min(99, Math.max(1, Math.trunc(quantity))) }
          : item,
      ),
    );
  }, []);

  const removeFromCart = useCallback((productId: string) => {
    setCart((items) => removeCartItem(items, productId));
  }, []);

  const toggleWishlist = useCallback((productId: string) => {
    setWishlist((items) =>
      items.includes(productId)
        ? items.filter((item) => item !== productId)
        : [...items, productId],
    );
  }, []);

  const checkout = useCallback(
    (idempotencyKey: string) => {
      if (cart.length === 0) return null;
      const order = createOrderFromCart(cart, idempotencyKey);
      setOrders((items) => [order, ...items]);
      setLastOrder(order);
      setCart([]);
      return order;
    },
    [cart],
  );

  const value = useMemo<DemoStoreValue>(
    () => ({
      cart,
      cartCount: cart.reduce((total, item) => total + item.quantity, 0),
      cartTotal: calculateCartTotal(cart),
      wishlist,
      user,
      orders,
      lastOrder,
      addToCart,
      updateQuantity,
      removeFromCart,
      toggleWishlist,
      login: (email) => setUser({ name: "DEMO 會員", email }),
      updateProfile: (update) =>
        setUser((current) => (current ? updateDemoUser(current, update) : current)),
      logout: () => setUser(null),
      checkout,
    }),
    [
      addToCart,
      cart,
      checkout,
      lastOrder,
      orders,
      removeFromCart,
      updateQuantity,
      user,
      wishlist,
      toggleWishlist,
    ],
  );

  return <DemoStoreContext.Provider value={value}>{children}</DemoStoreContext.Provider>;
}

export function useDemoStore(): DemoStoreValue {
  const value = useContext(DemoStoreContext);
  if (!value) throw new Error("useDemoStore must be used inside DemoStoreProvider");
  return value;
}
